import { ChangeDetectionStrategy, Component, computed, inject, ProviderToken, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { RouterOutlet } from '@angular/router';
import { connectState } from '@examdojo/angular/util';
import { FeatureFlagService } from '@examdojo/core/feature-flag';
import { WideLayoutComponent } from '@examdojo/layout';
import { ExamDojoFeatureFlag, ExamdojoFeatureFlags } from '@examdojo/models/feature-flag';
import { PlatformService } from '@examdojo/platform';
import { AssignmentQuery } from '@examdojo/assignment';
import { SidenavComponent, SidenavItem } from '@examdojo/shell';
import { combineLatest, map, Observable, of } from 'rxjs';
import { ActivityQuery } from '../activity/activity.query';
import { RootUrlParts } from '../app.model';
import { OpenCourseFormDialogEffectService } from '../effects/open-course-form-dialog-effect.service';
import {
  FEATURE_TO_ICON,
  FEATURE_TO_ICON_DARK,
  FEATURE_TO_LABEL_KEY,
  FEATURE_TO_LABEL_KEY_MOBILE,
  Features,
} from '../features/features.model';
import { ResumeActivityButtonComponent } from '../shared/resume-activity-button/resume-activity-button.component';
import { SHELL_EFFECTS } from './effects';
import { MobileMenuSheetDialogService } from './mobile-menu-sheet-dialog/mobile-menu-sheet-dialog.service';

interface FeaturePageConfig {
  feature: Features;
  flag: ExamDojoFeatureFlag | null;
  visible$?: Observable<boolean>;
}

@Component({
  selector: 'dojo-shell',
  imports: [RouterOutlet, SidenavComponent, ResumeActivityButtonComponent, WideLayoutComponent],
  templateUrl: './shell.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'block h-full safe-area-top safe-area-bottom' },
  providers: [...SHELL_EFFECTS, OpenCourseFormDialogEffectService],
})
export class ShellComponent {
  constructor(
    private readonly featureFlagService: FeatureFlagService<ExamdojoFeatureFlags>,
    private readonly activityQuery: ActivityQuery,
    private readonly platformService: PlatformService,
    private readonly mobileMenuSheetDialogService: MobileMenuSheetDialogService,
    private readonly assignmentQuery: AssignmentQuery,
  ) {}

  private readonly instantiatedServices = [...SHELL_EFFECTS, OpenCourseFormDialogEffectService].map(
    (service: ProviderToken<unknown>) => inject(service),
  );

  private readonly isMoreMenuOpen = signal(false);

  private readonly featurePages: FeaturePageConfig[] = [
    { feature: Features.TopicPractice, flag: null },
    { feature: Features.LearnHub, flag: null },
    { feature: Features.Exams, flag: null, visible$: this.assignmentQuery.hasAssignments$ },
    { feature: Features.Assessments, flag: null },
    { feature: Features.QuestionBank, flag: null },
    { feature: Features.Classes, flag: ExamDojoFeatureFlag.Classes },
  ];

  readonly sidenavItems$ = combineLatest([
    this.featureFlagService.selectAll(),
    this.platformService.isSmall$,
    ...this.featurePages.map(({ visible$ }) => visible$ ?? of(true)),
  ]).pipe(
    map(([allFlags, isMobile, ...visibilities]) =>
      this.featurePages
        .filter(({ flag, feature }, index) => {
          if (flag && !allFlags[flag]) {
            return false;
          }
          if (!(visibilities[index] as boolean)) {
            return false;
          }
          const mobileHiddenFeatures = [Features.Assessments, Features.Classes];
          if (isMobile && mobileHiddenFeatures.includes(feature)) {
            return false;
          }
          return true;
        })
        .map(
          ({ feature }): SidenavItem => ({
            route: feature,
            labelKey: FEATURE_TO_LABEL_KEY[feature],
            labelKeyMobile: FEATURE_TO_LABEL_KEY_MOBILE[feature],
            icon: FEATURE_TO_ICON[feature],
            iconDark: FEATURE_TO_ICON_DARK[feature],
          }),
        ),
    ),
  );

  readonly state = connectState({
    sidenavItems: this.sidenavItems$,
    hasOngoingActivity: this.activityQuery.hasOngoingActivity$,
    isMoreMenuOpen: toObservable(this.isMoreMenuOpen),
  });

  readonly settingsRoute = RootUrlParts.Settings;

  private readonly moreMenuItems = computed(() =>
    [Features.Assessments, Features.Classes].map(
      (feature): SidenavItem => ({
        route: feature,
        labelKey: FEATURE_TO_LABEL_KEY[feature],
        labelKeyMobile: FEATURE_TO_LABEL_KEY_MOBILE[feature],
        icon: FEATURE_TO_ICON[feature],
        iconDark: FEATURE_TO_ICON_DARK[feature],
      }),
    ),
  );

  async onMoreClicked() {
    if (this.isMoreMenuOpen()) {
      await this.mobileMenuSheetDialogService.dismiss();
      return;
    }

    this.isMoreMenuOpen.set(true);

    await this.mobileMenuSheetDialogService.open(
      { items: this.moreMenuItems(), settingsRoute: this.settingsRoute },
      () => this.isMoreMenuOpen.set(false),
    );
  }

  async onMenuItemClicked() {
    if (this.isMoreMenuOpen()) {
      await this.mobileMenuSheetDialogService.dismiss();
      this.isMoreMenuOpen.set(false);
    }
  }
}
